import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLoadSharedCartQuery, useAddSharedCartToMyCartMutation } from '../../services/cartApi';
import { useImage } from "../../context/ImageContext.js";
import Loader from '../../components/Loader';
import './SharedCart.css';
import { FavoriteContext } from '../../context/FavoriteContext.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as SolidHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart as RegularHeart } from '@fortawesome/free-regular-svg-icons';
import { useToast } from '../../context/ToastContext';
import { getApiUrl } from '../../config';

const SharedCart = () => {
  const { shareCode, sef } = useParams();
  const navigate = useNavigate();
  const { getImageUrl } = useImage();
  const { showToast } = useToast();
  const { favorites, addToFavorite, removeFavorite } = useContext(FavoriteContext);

  const { data: sharedCartData, isLoading, isError, error, isSuccess } = useLoadSharedCartQuery(shareCode, {
    skip: !shareCode
  });
  const [addSharedCartToMyCart, { isLoading: isAdding }] = useAddSharedCartToMyCartMutation();

  const [selectedItems, setSelectedItems] = useState({});
  const [selectAll, setSelectAll] = useState(true);
  const [selectedSizes, setSelectedSizes] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [ringPricing, setRingPricing] = useState({});  // Her ürün için ring pricing bilgisi

  useEffect(() => {
    if (isSuccess && sharedCartData && sharedCartData.length > 0 && shareCode) {
      document.cookie = `share_ref=${shareCode}; path=/; max-age=${24 * 60 * 60}`;
    }
  }, [isSuccess, sharedCartData, isError, error, shareCode]);

  useEffect(() => {
    if (isSuccess && sharedCartData && sharedCartData.length > 0) {
      const initialSelected = {};
      const initialSizes = {};

      sharedCartData.forEach(async (item, index) => {
        initialSelected[index] = true;
        if (isRingProduct(item)) {

          // Backend'den gelen ring_size değerini öncelikli olarak kullan
          let selectedSize = 15; // Varsayılan değer
          
          // Önce direkt ring_size alanını kontrol et
          if (item.ring_size && item.ring_size !== null && item.ring_size !== undefined) {
            selectedSize = Number(item.ring_size);
          } 
          // Sonra size alanını kontrol et
          else if (item.size && item.size !== null && item.size !== undefined) {
            selectedSize = Number(item.size);
          }
          // Son olarak diger alanındaki JSON'dan ring_size'ı parse et
          else if (item.diger && item.diger !== null && item.diger !== undefined) {
            try {
              const digerData = JSON.parse(item.diger);
              if (digerData.ring_size && digerData.ring_size !== null && digerData.ring_size !== undefined) {
                selectedSize = Number(digerData.ring_size);
              }
            } catch (parseError) {
              console.warn(`Item ${index} - diger alanı parse edilemedi:`, parseError);
            }
          }
          
          initialSizes[index] = selectedSize;
          
          // İlk yükleme için ring pricing hesapla
          await fetchRingSizePrice(item.id, selectedSize, index);
        }
      });

      setSelectedItems(initialSelected);
      setSelectedSizes(initialSizes);
      setSelectAll(true);
    }
  }, [isSuccess, sharedCartData]);

  // Ring size değiştiğinde backend'den fiyat hesaplama
  const fetchRingSizePrice = async (productId, selectedSize, index) => {
    try {
      const response = await fetch(`${getApiUrl()}/ring-size-price`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: productId,
          selected_size: selectedSize
        })
      });

      if (response.ok) {
        const data = await response.json();
        setRingPricing(prev => ({
          ...prev,
          [index]: data
        }));
        return data.price_difference;
      }
    } catch (error) {
      console.error('Error fetching ring size price:', error);
    }
    return 0;
  };

  useEffect(() => {
    const calculateNewTotalPrice = () => {
      if (!sharedCartData || sharedCartData.length === 0) {
        setTotalPrice(0);
        return;
      }

      let total = 0;
      sharedCartData.forEach((item, index) => {
        if (selectedItems[index]) {
          let itemPrice = parseFloat(item.fiyat || 0);
          
          // Yüzük ürünü ise ve ölçü değişmişse ring pricing ekle
          if (isRingProduct(item) && ringPricing[index]) {
            itemPrice += ringPricing[index].price_difference || 0;
          }

          total += itemPrice;
        }
      });
      setTotalPrice(total);
    };
    calculateNewTotalPrice();
  }, [selectedItems, selectedSizes, sharedCartData, isSuccess, ringPricing]);

  const handleContinueShopping = () => {
    navigate('/');
  };

  const toggleSelectItem = (index, event) => {
    if (event) {
      event.stopPropagation();
    }

    const newSelectedItems = {
      ...selectedItems,
      [index]: !selectedItems[index]
    };

    setSelectedItems(newSelectedItems);

    const allSelected = Object.values(newSelectedItems).every(value => value === true);
    setSelectAll(allSelected);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    const newSelectedItems = {};
    if (sharedCartData) {
      sharedCartData.forEach((_, index) => {
        newSelectedItems[index] = newSelectAll;
      });
    }
    setSelectedItems(newSelectedItems);
  };

  const getSelectedItems = () => {
    if (!isSuccess || !sharedCartData || !Array.isArray(sharedCartData)) {
      console.error("getSelectedItems: sharedCartData geçersiz formatta veya yüklenmemiş.", sharedCartData);
      return [];
    }

    if (!selectedItems) {
      return [];
    }

    try {
      return sharedCartData
        .filter((_, index) => selectedItems[index])
        .map((item, index) => {
          const currentItem = sharedCartData.find((_, i) => i === index);
          if (isRingProduct(currentItem) && selectedSizes[index]) {
            return { ...currentItem, size: selectedSizes[index] };
          }
          return currentItem;
        });
    } catch (error) {
      console.error("getSelectedItems hatası:", error);
      return [];
    }
  };

  const isRingProduct = (item) => {
    if (!item) return false;
    if (item.kategori) {
      if (typeof item.kategori === 'string') {
        if (item.kategori.includes("75") ||
          item.kategori.includes("103") ||
          item.kategori.includes("112") ||
          item.kategori.toLowerCase().includes("yüzük") ||
          item.kategori.toLowerCase().includes("yuzuk")) {
          return true;
        }
      }

      if (Array.isArray(item.kategori)) {
        const isRing = item.kategori.some(kat =>
          kat.includes("75") ||
          kat.includes("103") ||
          kat.includes("112") ||
          kat.toLowerCase().includes("yüzük") ||
          kat.toLowerCase().includes("yuzuk")
        );

        if (isRing) {
          return true;
        }
      }
    }

    if (item.baslik) {
      const title = item.baslik.toLowerCase();
      if (title.includes("yüzük") ||
        title.includes("yuzuk") ||
        title.includes("alyans") ||
        title.includes("söz") ||
        title.includes("nişan")) {
        return true;
      }
    }
    return false;
  };

  const handleSizeChange = async (index, size, event) => {
    if (event) {
      event.stopPropagation();
    }

    setSelectedSizes(prev => ({
      ...prev,
      [index]: parseInt(size)
    }));

    // Ring product ise backend'den fiyat hesapla
    const item = sharedCartData[index];
    if (item && isRingProduct(item)) {
      await fetchRingSizePrice(item.id, parseInt(size), index);
    }
  };

  const handleAddSelectedToCart = async () => {
    const selectedProducts = getSelectedItems();
    if (!selectedProducts || selectedProducts.length === 0) {
      showToast('Lütfen en az bir ürün seçin', 'warning');
      return;
    }

    try {
      const result = await addSharedCartToMyCart(selectedProducts).unwrap();
      showToast(result.message, result.success ? 'success' : 'error');
    } catch (err) {
      console.error('Sepete ekleme hatası:', err);
      showToast(err.data?.message || err.message || 'Ürünler sepete eklenirken bir hata oluştu', 'error');
    }
  };

  const handleBuySelected = async () => {
    const selectedProducts = getSelectedItems();
    if (!selectedProducts || selectedProducts.length === 0) {
      showToast('Lütfen en az bir ürün seçin', 'warning');
      return;
    }

    try {
      const result = await addSharedCartToMyCart(selectedProducts).unwrap();
      if (result.success) {
        showToast('Ürünler sepete eklendi', 'success');
        navigate('/card');
      } else {
        showToast(result.message || 'Ürünler sepete eklenemedi.', 'error');
      }
    } catch (err) {
      console.error('Hemen satın alma hatası:', err);
      showToast(err.data?.message || err.message || 'İşlem sırasında bir hata oluştu', 'error');
    }
  };

  const isFavorite = (product) => {
    return favorites && favorites.length > 0 && favorites.some(favProduct =>
      String(favProduct.urun_id) === String(product.id)
    );
  };

  const handleFavoriteToggle = async (event, product) => {
    event.stopPropagation();
    event.preventDefault();

    try {
      if (isFavorite(product)) {
        const favoriteProduct = favorites.find(fav =>
          String(fav.urun_id) === String(product.id)
        );
        if (favoriteProduct) {
          await removeFavorite(favoriteProduct);
        }
      } else {
        await addToFavorite(product);
      }
    } catch (err) {
      console.error('Favori işlemi sırasında hata:', err);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    const errorMessage = error?.data?.message || error?.data?.error || error?.message || 'Paylaşılan sepet yüklenemedi.';
    return (
      <div className="shared-cart-container">
        <div className="shared-cart-error">
          <div className="error-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="#FF6B6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M12 8V13" stroke="#FF6B6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M11.9945 16H12.0035" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2>Sepet Bulunamadı</h2>
          <p>{errorMessage}</p>
          <p>Bu paylaşım linki geçersiz olabilir veya süresi dolmuş olabilir.</p>
          <button className="shared-cart-continue-btn" onClick={handleContinueShopping}>
            Alışverişe Devam Et
          </button>
        </div>
      </div>
    );
  }

  if (isSuccess && sharedCartData && sharedCartData.length > 0) {
    return (
      <div className="shared-cart-container">
        <div className="shared-cart-header mt-md-3 mt-1">
          <h1>Paylaşılan Sepet</h1>
          <p>Bu sepet sizinle paylaşıldı. Ürünleri seçerek sepetinize ekleyebilirsiniz.</p>
          <div className="select-all-container">
            <label className="select-all-label">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              />
              <span className="select-all-text">Tümünü Seç</span>
            </label>
          </div>
        </div>
        <div className="shared-cart-items">
          {sharedCartData.map((item, i) => (
            <div
              key={i}
              className={`shared-cart-item ${isRingProduct(item) ? 'ring-product' : ''}`}
              style={{
                border: selectedItems[i] ? '2px solid #4CAF50' : 'none',
                backgroundColor: selectedItems[i] ? '#f0f9f0' : 'transparent',
                transition: 'border 0.2s ease-in-out, background-color 0.2s ease-in-out',
                boxShadow: selectedItems[i] ? '0 0 10px rgba(76, 175, 80, 0.5)' : '0 4px 10px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              <div
                className="item-checkbox"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleSelectItem(i, e);
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedItems[i] || false}
                  readOnly
                />
              </div>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFavoriteToggle(e, item);
                }}
                className="favorite-icon-shared-cart"
              >
                <FontAwesomeIcon
                  icon={isFavorite(item) ? SolidHeart : RegularHeart}
                  style={{ color: isFavorite(item) ? 'red' : 'gray', fontSize: window.innerWidth <= 768 ? '18px' : '24px' }}
                />
              </div>
              <a className="item-image cursor-pointer"
                href={`/urun/${item.sef}`}>
                <img
                  src={getImageUrl(item.image?.i1 || '')}
                  alt={item.baslik}
                  onError={(e) => {
                    e.target.onerror = null;
                  }}
                />
              </a>
              <div className="item-details">
                <div className="item-details-top">
                  <h3>{item.baslik}</h3>
                  {isRingProduct(item) && (
                    <div className="item-size-container" onClick={(e) => e.stopPropagation()}>
                      <span className="size-select-label">Ölçü:</span>
                      <select
                        className="size-select"
                        value={selectedSizes[i] || 15}
                        onChange={(e) => handleSizeChange(i, e.target.value, e)}
                      >
                        {Array.from({ length: 25 }, (_, index) => index + 6).map(size => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {isRingProduct(item) && ringPricing[i] && (
                  <p className="size-price-difference-shared-cart">
                    {ringPricing[i].price_difference > 0 
                      ? ringPricing[i].description 
                      : 'Standart ölçü seçildi.'
                    }
                  </p>
                )}
                <div className="ratingg-shared-cart">★★★★★</div>

                <p className="item-price">
                  {(() => {
                    let finalPrice = parseFloat(item.fiyat) || 0;
                    
                    // Ring pricing varsa ekle
                    if (isRingProduct(item) && ringPricing[i]) {
                      finalPrice += ringPricing[i].price_difference || 0;
                    }
                    
                    return finalPrice.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                  })()} TL
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="shared-cart-summary">
          <div className="summary-item-total">
            <span>Toplam Tutar:</span>
            <span className="total-price">{totalPrice.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} TL</span>
          </div>
        </div>

        <div className="shared-cart-actions">
          <button
            className="shared-cart-button add-to-cart"
            onClick={handleAddSelectedToCart}
            disabled={isAdding}
          >
            {isAdding ? 'Ekleniyor...' : 'Seçili Ürünleri Sepete Ekle'}
          </button>
          <button
            className="shared-cart-button buy-now"
            onClick={handleBuySelected}
            disabled={isAdding}
          >
            {isAdding ? 'İşleniyor...' : 'Seçili Ürünleri Satın Al'}
          </button>
          <button
            className="shared-cart-button continue-shopping"
            onClick={handleContinueShopping}
          >
            Alışverişe Devam Et
          </button>
        </div>
      </div>
    );
  }

  if (isSuccess && (!sharedCartData || sharedCartData.length === 0)) {
    return (
      <div className="shared-cart-container">
        <div className="shared-cart-empty">
          <div className="empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 7.67001V6.70001C7.5 4.45001 9.31 2.24001 11.56 2.03001C14.24 1.77001 16.5 3.88001 16.5 6.51001V7.89001" stroke="#ccc" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9 22H15C19.02 22 19.74 20.39 19.95 18.43L20.7 12.43C20.97 9.99 20.27 8 16 8H8C3.73 8 3.03 9.99 3.3 12.43L4.05 18.43C4.26 20.39 4.98 22 9 22Z" stroke="#ccc" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2>Paylaşılan Sepet Boş</h2>
          <p>Bu sepette hiç ürün bulunmuyor.</p>
          <button className="shared-cart-continue-btn" onClick={handleContinueShopping}>
            Alışverişe Devam Et
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-cart-container">
      <div className="shared-cart-loading">
        <h2>Paylaşılan Sepet Yükleniyor...</h2>
        <p>Lütfen bekleyin, paylaşılan ürünler yükleniyor.</p>
      </div>
    </div>
  );
};

export default SharedCart; 